import { noop } from 'lodash';
import React, { useState, createRef, useRef } from 'react';
import { Container } from '@fiverr-private/layout_components';
import { Input } from '@fiverr-private/forms';
import * as classes from './styles.ve.css';

const KEY_CODE = {
    BACKSPACE: 'Backspace',
    LEFT_ARROW: 'ArrowLeft',
    RIGHT_ARROW: 'ArrowRight',
    ENTER: 'Enter',
};

const isOnlyDigits = (value: string) => /^\d+$/.test(value);

interface CodeInputProps {
    codeLength: number;
    disabled?: boolean;
    autoFocus?: boolean;
    isError?: boolean;
    onChange?: (value: string) => void;
    onSubmit?: (value: string) => void;
    firstInputRef?: React.RefObject<HTMLInputElement>;
}

export const CodeInput = ({
    codeLength,
    disabled = false,
    autoFocus = false,
    isError = false,
    onChange = noop,
    onSubmit = noop,
    firstInputRef,
}: CodeInputProps) => {
    const [inputValues, setInputValues] = useState<string[]>(() => Array(codeLength).fill(''));
    const inputRefs = useRef<React.RefObject<HTMLInputElement>[]>(
        [...Array(codeLength)].map((_, i) => {
            if (i === 0 && firstInputRef) {
                return firstInputRef;
            }
            return createRef<HTMLInputElement>();
        })
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, position: number) => {
        const value = e.target.value?.slice(0, codeLength);

        if (!isOnlyDigits(value)) {
            return;
        }

        const inputValuesCopy = inputValues.slice();

        if (value.length > 1) {
            value.split('').forEach((char, i) => {
                if (position + i < codeLength) {
                    inputValuesCopy[position + i] = char;
                }
            });
        } else {
            inputValuesCopy[position] = value;
        }

        const newTarget = inputRefs.current[Math.min(position + value.length, codeLength - 1)];
        moveFocus(newTarget);

        setInputValues(inputValuesCopy);

        onChange(inputValuesCopy.join(''));
    };

    const moveFocus = (targetRef: React.RefObject<HTMLInputElement>) => {
        if (targetRef && targetRef.current) {
            targetRef.current.focus();
        }
    };

    const deleteInputValue = (index: number) => {
        const inputValuesCopy = inputValues.slice();
        inputValuesCopy[index] = '';

        setInputValues(inputValuesCopy);

        onChange(inputValuesCopy.join(''));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        const nextTarget = inputRefs.current[Math.min(index + 1, codeLength - 1)];
        const prevTarget = inputRefs.current[Math.max(index - 1, 0)];

        switch (e.code) {
            case KEY_CODE.BACKSPACE:
                e.preventDefault();
                deleteInputValue(index);
                moveFocus(prevTarget);
                break;

            case KEY_CODE.LEFT_ARROW:
                e.preventDefault();
                moveFocus(prevTarget);
                break;

            case KEY_CODE.RIGHT_ARROW:
                e.preventDefault();
                moveFocus(nextTarget);
                break;

            case KEY_CODE.ENTER:
                const codeString = inputValues.join('');
                if (codeString.length === codeLength) {
                    e.preventDefault();
                    onSubmit(codeString);
                }
                break;

            default:
                break;
        }
    };

    return (
        <Container display="flex" className={classes.codeInputGroup}>
            {inputValues.map((value, i) => (
                <Input
                    value={value}
                    ref={inputRefs.current[i]}
                    autoFocus={autoFocus && i === 0}
                    key={i}
                    min={0}
                    max={9}
                    maxLength={i === codeLength - 1 ? 1 : codeLength}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => handleChange(e, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    disabled={disabled}
                    isError={isError}
                    className={classes.codeInput}
                />
            ))}
        </Container>
    );
};
